import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddTicketTab({ token, onDone }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [dates, setDates] = useState([]);
  const [dateId, setDateId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [over18, setOver18] = useState(false);
  const [allergies, setAllergies] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/dates`, auth).then((r) => {
      setDates(r.data.dates || []);
      const first = (r.data.dates || []).find((d) => d.remaining > 0) || (r.data.dates || [])[0];
      if (first) setDateId(first.id);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!dateId) return toast.error("Choose a date.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email.");
    if (!over18) return toast.error("Confirm the guest is 18 or over.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/manual`, {
        email, name, quantity, over18, allergies, date_id: dateId, origin_url: window.location.origin,
      }, auth);
      toast.success(`Ticket ${data.booking_ref} created${data.email_sent ? " and emailed to " + data.email : " (email failed — try resend)"}.`);
      setEmail(""); setName(""); setQuantity(1); setOver18(false); setAllergies("");
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create ticket.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto" data-testid="add-ticket-tab">
      <div className="ornate-frame bg-[var(--surface)] p-8">
        <h2 className="font-display text-3xl text-[var(--cream)] mb-1">Add a ticket</h2>
        <p className="font-body text-sm text-[var(--muted)] mb-6">Create a booking manually (e.g. paid at the bar). The guest is emailed a QR ticket. No online payment is taken.</p>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Date</label>
            <select data-testid="add-date-select" value={dateId} onChange={(e) => setDateId(e.target.value)} className="field-input">
              {dates.map((d) => (
                <option key={d.id} value={d.id} className="bg-[var(--surface)]">{d.label} — {d.remaining} left</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Guest name</label>
            <input data-testid="add-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="field-input" />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Guest email</label>
            <input data-testid="add-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@example.com" className="field-input" required />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Number of people</label>
            <input data-testid="add-qty-input" type="number" min={1} max={200} value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value || "1", 10)))} className="field-input" />
          </div>
          <div>
            <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-3">Allergies (optional)</label>
            <textarea data-testid="add-allergies-input" value={allergies} onChange={(e) => setAllergies(e.target.value)} rows={2} className="field-input resize-none" placeholder="Any allergies or dietary needs" />
          </div>
          <button type="button" data-testid="add-over18-checkbox" onClick={() => setOver18((v) => !v)} className="flex items-center gap-3 w-full text-left">
            <span className={`w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${over18 ? "bg-[var(--gold)] border-[var(--gold)]" : "border-[rgba(201,162,74,0.5)]"}`}>
              {over18 && <Check className="w-4 h-4 text-[var(--bg)]" />}
            </span>
            <span className="font-body text-sm text-[var(--cream)]">Guest confirms they are 18 years or over.</span>
          </button>
          <button type="submit" data-testid="add-submit-btn" disabled={loading}
            className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-300 disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Send className="w-4 h-4" /> Create &amp; email ticket</>}
          </button>
        </form>
      </div>
    </div>
  );
}
